import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { AlertTriangle, ArrowRight, Sparkles } from "lucide-react";

const SEVERITY_CHIP = {
  high: "bg-red-500/15 text-red-300 border-red-500/40",
  medium: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  low: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

/**
 * Signal of the Week — homepage card showing the highest-severity Money
 * Flow risk signal from the last 7 days. Drives organic traffic to the
 * /money-flow/signals feed + Pro upgrade hook.
 *
 * Gracefully returns null if no signal in the last 7 days — no empty state.
 */
export default function SignalOfTheWeek() {
  const [signal, setSignal] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/money-flow/signal-of-the-week`)
      .then((r) => setSignal(r.data?.signal))
      .catch(() => setError(true));
  }, []);

  if (error || !signal) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" data-testid="signal-of-the-week">
      <Link
        to="/money-flow/signals"
        className="block rounded-2xl border border-rp-accent/30 bg-gradient-to-br from-rp-accent/[0.06] to-transparent p-5 sm:p-6 hover:border-rp-accent/60 transition-colors"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-rp-accent/15 ring-1 ring-rp-accent/30">
            <Sparkles className="h-4 w-4 text-rp-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-rp-accent font-semibold">Signal of the week</span>
              <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-full border ${SEVERITY_CHIP[signal.severity] || ""}`} data-testid="sotw-severity">
                {signal.severity}
              </span>
              <span className="text-[10px] uppercase font-mono text-rp-textFaint">· {signal.domain}</span>
              {signal.state_codes?.length > 0 && (
                <span className="text-[10px] font-mono text-rp-textFaint">· {signal.state_codes.join(", ")}</span>
              )}
            </div>
            <div className="mt-2 text-rp-text text-base sm:text-lg leading-snug font-semibold" data-testid="sotw-headline">
              {signal.headline}
            </div>
            {signal.reason && (
              <div className="mt-2 text-xs text-rp-textDim leading-relaxed line-clamp-2">{signal.reason}</div>
            )}
            <div className="mt-3 inline-flex items-center gap-1 text-xs text-rp-accent font-semibold">
              See the full feed <ArrowRight className="h-3 w-3" />
            </div>
          </div>
          <AlertTriangle className="h-4 w-4 text-rp-textFaint hidden sm:block" />
        </div>
      </Link>
    </section>
  );
}
